import { useId, useState, type InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Visible field label. Rendered as a real `<label for=…>` tied to the input.
   *
   * This component renders its own label rather than being dropped inside a caller's `<label>`:
   * a `<label>` that wraps interactive content folds that content's text into the *input's*
   * accessible name, so the show/hide button turned the field's name into
   * "Password Show password". Owning the label here keeps the input named "Password" and the
   * button named "Show password", which is what a screen-reader user needs to hear. */
  label: string;
};

/** FR-078: a password field with a show/hide toggle, used by every sign-in form and the CO
 * Member's own password-change form so the behaviour is identical everywhere. */
export function PasswordInput({ label, className = "", ...props }: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const id = useId();

  const toggleLabel = visible
    ? t("common.hidePassword", "Hide password")
    : t("common.showPassword", "Show password");

  return (
    <div className="flex flex-col gap-1 text-sm text-body">
      <label htmlFor={id}>{label}</label>
      <div className="relative">
        <input
          {...props}
          id={id}
          type={visible ? "text" : "password"}
          className={`w-full rounded border border-surface-alt py-2 pl-3 pr-10 ${className}`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={toggleLabel}
          aria-pressed={visible}
          title={toggleLabel}
          // Kept out of the tab order: a sighted keyboard user tabbing through a sign-in form
          // wants Email → Password → Submit, not a detour through a visual affordance. It stays
          // reachable by click and is still announced when a screen reader browses the form.
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-body hover:text-ink"
        >
          {/* Inline SVG rather than an icon dependency — two icons doesn't justify a package. */}
          {visible ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
